import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail, Sparkles } from "lucide-react";

export default function NewsletterPage() {
  const { user } = useAuth();
  const [nl, setNl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/newsletter/latest");
      setNl(data.newsletter);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/newsletter/generate");
      await load();
    } finally {
      setGenerating(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { toast } = await import("sonner");
      const { data } = await api.post("/emails/newsletter/send");
      if (data.skipped) {
        toast.error("Envoi d'email désactivé (clé Resend manquante)");
      } else {
        toast.success(`Email envoyé à ${user?.email}`);
      }
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF453A] mb-3">
        Récap hebdomadaire · Générée par IA
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-4" data-testid="newsletter-title">
        La semaine SEO, en 4 minutes.
      </h1>
      <p className="text-white/50 mb-10 max-w-2xl">
        Chaque lundi matin, Claude Sonnet 4.5 synthétise la semaine SEO écoulée en une lecture éditoriale structurée.
      </p>

      {loading ? (
        <div className="text-white/40 font-mono text-sm">Chargement…</div>
      ) : !nl ? (
        <div className="border border-white/10 bg-[#121215] p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
          <Mail className="w-10 h-10 text-white/30 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display text-2xl font-bold mb-2">La première édition arrive lundi.</div>
            <p className="text-white/50">Le récap hebdomadaire est généré tous les lundis à 07:00 UTC à partir de la veille de la semaine.</p>
          </div>
          {user?.subscribed && (
            <button
              onClick={generate}
              disabled={generating}
              data-testid="newsletter-generate-btn"
              className="bg-white text-black px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[#FF453A] transition-colors disabled:opacity-40"
            >
              {generating ? "Génération…" : "Générer maintenant"}
            </button>
          )}
        </div>
      ) : (
        <article className="relative border border-white/10 bg-[#121215] p-8 md:p-12" data-testid="newsletter-article">
          <div className="beam-border absolute inset-0 pointer-events-none" />
          <div className="relative z-10">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">
              {nl.week_start} → {nl.week_end}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05] mb-8">
              {nl.headline}
            </h2>
            {nl.locked ? (
              <div className="border border-[#FF453A]/40 bg-[#FF453A]/5 p-6">
                <div className="flex items-center gap-2 text-[#FF453A] font-mono text-xs uppercase tracking-widest mb-3">
                  <Lock className="w-4 h-4" /> Récap réservé aux abonnés
                </div>
                <p className="text-white/70 mb-6">{nl.intro}</p>
                <button
                  onClick={subscribe}
                  data-testid="newsletter-subscribe-btn"
                  className="bg-[#FF453A] text-black px-6 py-3 font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors"
                >
                  Se connecter · gratuit
                </button>
              </div>
            ) : (
              <>
                <p className="text-lg text-white/80 leading-relaxed mb-12 max-w-3xl">{nl.intro}</p>
                <div className="space-y-10">
                  {nl.sections?.map((s, i) => (
                    <section key={i} data-testid={`newsletter-section-${i}`} className="border-t border-white/10 pt-8">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-mono text-[#FF453A] text-sm">{String(i + 1).padStart(2, "0")}</span>
                        <h3 className="font-display text-2xl font-bold">{s.title}</h3>
                      </div>
                      <p className="text-white/75 leading-relaxed max-w-3xl">{s.body}</p>
                    </section>
                  ))}
                </div>
                {user?.subscribed && (
                  <div className="mt-10 flex flex-wrap items-center gap-3">
                    <button
                      onClick={generate}
                      disabled={generating}
                      data-testid="newsletter-regenerate-btn"
                      className="text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white"
                    >
                      {generating ? "Génération…" : "↻ Régénérer"}
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                      onClick={sendEmail}
                      disabled={sending}
                      data-testid="newsletter-send-email-btn"
                      className="text-[10px] font-mono uppercase tracking-widest text-[#FF8C42] hover:text-white flex items-center gap-1"
                    >
                      {sending ? "Envoi…" : "✉ M'envoyer par email"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </article>
      )}
    </div>
  );
}
